import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MemberDto } from '@careapp-group/ng-sdk';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable, switchMap } from 'rxjs';
import { auth } from '../../../store/auth/auth.selectors';
import { Conversations } from '../../../store/conversations/conversations.actions';
import { conversationMembers } from '../../../store/conversations/conversations.selectors';
export interface ParticipantSelectDialogData {
  // IDs of users that will be pre-selected, but can be toggled off.
  initialSelectedIds: string[];
  // IDs of users that cannot be toggled.
  disabledIds: string[];
}

@Component({
  selector: 'ca-conversation-participant-select-dialog',
  styleUrls: ['./conversation-participant-select-dialog.component.scss'],
  templateUrl: './conversation-participant-select-dialog.component.html',
  standalone: false,
})
export class ConversationParticipantSelectDialogComponent implements OnInit, OnDestroy {
  private searchSubject = new BehaviorSubject<string>('');
  private _selectedIdFilter: (item: MemberDto, index: number) => boolean;
  _search = '';

  public allMembers$: Observable<MemberDto[]>;
  public filteredMembers$: Observable<MemberDto[]>;
  public currentUser$: Observable<MemberDto>;
  public selectedIds: string[] = [];
  public disabledIds: string[] = [];
  public selectedMembers: MemberDto[] = [];

  constructor(
    public store: Store<any>,
    @Inject(MAT_DIALOG_DATA) public data: ParticipantSelectDialogData
  ) {
    this._selectedIdFilter = (item: MemberDto, index: number) =>
      item && this.selectedIds.includes(item.id);
  }

  ngOnInit() {
    this.selectedIds = [...(this.data.initialSelectedIds ?? [])];
    this.disabledIds = [...(this.data.disabledIds ?? [])];
    this.store.dispatch(Conversations.requestConversationMembers());
    this.allMembers$ = this.store.select(conversationMembers);
    this.currentUser$ = this.store.select(auth.getLoggedInUser);

    this.filteredMembers$ = combineLatest([
      this.allMembers$,
      this.searchSubject.asObservable(),
    ]).pipe(
      map(([members, search]) => {
        if (!members) {
          return [];
        }

        if (!Array.isArray(members)) {
          return [];
        }

        if (!search || !search.length) {
          return members;
        }

        const filtered = members.filter((member) => {
          return (
            (member.firstName?.toLowerCase() ?? '') +
            ' ' +
            (member.lastName?.toLowerCase() ?? '')
          ).includes(search.toLowerCase());
        });
        return filtered;
      })
    );
  }

  toggle(member: MemberDto) {
    const isSelected = !!this.selectedIds.find((id) => id === member.id);

    this.selectedIds = isSelected
      ? [...this.selectedIds.filter((id) => id !== member.id)]
      : [...this.selectedIds, member.id];

    this.selectedMembers = isSelected
      ? [...this.selectedMembers.filter((m) => m.id !== member.id)]
      : [...this.selectedMembers, member];
  }

  set search(value: string) {
    this._search = value;
    this.searchSubject.next(value);
  }
  get search() {
    return this._search;
  }

  get selectedIdFilter() {
    return this._selectedIdFilter;
  }

  trackByMemberId(index: number, member: MemberDto): string {
    return member.id;
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }
}
